import Feature from 'ol/Feature';
import type Map from 'ol/Map';
import type MapBrowserEvent from 'ol/MapBrowserEvent';
import type { EventsKey } from 'ol/events';
import { altKeyOnly, singleClick } from 'ol/events/condition';
import type Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Modify from 'ol/interaction/Modify';
import VectorLayer from 'ol/layer/Vector';
import { unByKey } from 'ol/Observable';
import VectorSource from 'ol/source/Vector';
import type { StyleFunction } from 'ol/style/Style';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';

/**
 * Temporary edit of ONE planning object's geometry with OpenLayers Modify: drag vertices, drag a segment to insert a
 * vertex, delete a vertex (Alt+clic, or a tap while «Borrar vértices» is on; Modify itself refuses to go below two
 * vertices on a line or three on a polygon). Points are dragged, or moved with a tap. It works on a CLONE in its own
 * layer: the object in the project is untouched until the caller applies the result.
 */
export class PlanningGeometryEditor {
  private readonly source = new VectorSource<Feature<Geometry>>();
  private readonly layer = new VectorLayer({ source: this.source, properties: { role: 'planning-edit' }, zIndex: 10_040 });
  private feature: Feature<Geometry> | null = null;
  private modify: Modify | null = null;
  private keys: EventsKey[] = [];
  private undoStack: Geometry[] = [];
  private deleteMode = false;

  constructor(private readonly map: Map) {}

  get active(): boolean { return this.feature !== null; }
  get canUndo(): boolean { return this.undoStack.length > 0; }
  get deletingVertices(): boolean { return this.deleteMode; }

  /** Starts editing a clone of `geometry`; `changed` receives the live geometry on every modification. */
  start(geometry: Geometry, style: StyleFunction, changed: (geometry: Geometry) => void): void {
    this.stop();
    const feature = new Feature<Geometry>({ geometry: geometry.clone() });
    this.feature = feature; this.undoStack = []; this.deleteMode = false;
    this.source.addFeature(feature);
    this.layer.setStyle(style);
    if (!this.map.getLayers().getArray().includes(this.layer)) this.map.addLayer(this.layer);
    const isPoint = geometry.getType() === 'Point';
    this.modify = new Modify({
      source: this.source,
      pixelTolerance: 14,
      style: new Style({ image: new CircleStyle({ radius: 7, fill: new Fill({ color: 'rgba(255,255,255,.9)' }), stroke: new Stroke({ color: '#ff00a8', width: 2.5 }) }) }),
      deleteCondition: (event) => !isPoint && singleClick(event) && (altKeyOnly(event) || this.deleteMode),
      insertVertexCondition: () => !this.deleteMode,
    });
    this.map.addInteraction(this.modify);
    this.keys = [
      this.modify.on('modifystart', () => this.remember()),
      feature.on('change', () => { const current = feature.getGeometry(); if (current && this.feature === feature) changed(current); }),
    ];
    // A tap/click moves a point there (dragging also works); on lines and polygons clicks do nothing else.
    if (isPoint) this.keys.push(this.map.on('singleclick', (event: MapBrowserEvent) => this.movePoint(event.coordinate)));
  }

  /** Current edited geometry (a clone), or null when not editing. */
  geometry(): Geometry | null { return this.feature?.getGeometry()?.clone() ?? null; }

  setDeleteMode(enabled: boolean): void { this.deleteMode = enabled && this.active; }

  /** Reverts the last modification of this edit session. */
  undo(): boolean {
    const previous = this.undoStack.pop();
    if (!previous || !this.feature) return false;
    this.feature.setGeometry(previous);
    return true;
  }

  stop(): void {
    unByKey(this.keys); this.keys = [];
    if (this.modify) { this.map.removeInteraction(this.modify); this.modify = null; }
    this.source.clear(true);
    if (this.map.getLayers().getArray().includes(this.layer)) this.map.removeLayer(this.layer);
    this.feature = null; this.undoStack = []; this.deleteMode = false;
  }

  private remember(): void {
    const geometry = this.feature?.getGeometry(); if (geometry) this.undoStack.push(geometry.clone());
  }

  private movePoint(coordinate: readonly number[]): void {
    const geometry = this.feature?.getGeometry();
    if (!(geometry instanceof Point) || coordinate.length < 2) return;
    this.remember();
    geometry.setCoordinates([coordinate[0]!, coordinate[1]!]);
  }
}
